import { isEmpty } from "lodash";
import { createTime24for30min } from "./functions";
import { getAccountTimeFormatDate, getTimeZoneOptions } from "./timeUtil";
import { UserGroupUserPermission } from "api/interfaces/userInterface.interface";
import {
  DISCOUNT_TIER,
  STORAGE_LOCATION,
  SUBSCRIPTION_TYPE,
} from "api/interfaces/subscription.interface";
import { changeServiceName } from "hook/subscription/useKeyType";
import {
  BILLING_TYPE,
  PLAN_TYPE,
} from "api/interfaces/planInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { COVA_MODEL } from "api/interfaces/managerInterface.interface";
import { ADMINUNLOCK_MODEL } from "api/interfaces/helpInterface.interface";
import { initialState as initialAccountState } from "redux/reducers/account/accountSlice";

export interface Option {
  readonly value: string;
  readonly label: string;
  readonly isDisabled?: boolean;
}

export interface UserGroupSelectOption extends Option {
  userPermission: UserGroupUserPermission;
}

export interface SelectListInterface {
  title: string;
  desc: string;
  address: string;
}

export const intervalOptions: Option[] = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
];

export const intervalOneOptions: Option[] = [
  { value: "1", label: "Past 1 Day" },
  { value: "7", label: "Past 1 Week" },
  { value: "30", label: "Past 1 Month" },
];

export const addIntervalOptions: Option[] = [
  { value: "WEEK", label: "Weekly" },
  { value: "MONTH", label: "Monthly" },
];

export const intervalMonthOptions: Option[] = [
  { value: "1", label: "1 Month" },
  { value: "3", label: "3 Months" },
  { value: "6", label: "6 Months" },
  // { value: "9", label: "9 Months" },
];

export const accountLevelOptionsAll: Option[] = [
  { value: "DW", label: "DW" },
  { value: "CP", label: "CP" },
  { value: "SP", label: "Sub CP" },
  { value: "EU", label: "EU" },
];
export const accountLevelOptions = (parentLevel: LEVEL_TYPE): Option[] => {
  if (parentLevel === LEVEL_TYPE.SP) {
    return [{ value: "EU", label: "EU" }];
  }
  if (parentLevel === LEVEL_TYPE.CP) {
    return [
      { value: "EU", label: "EU" },
      { value: "CP", label: "Sub CP" },
    ];
  }
  return [{ value: "CP", label: "CP" }];
};
export const accountLevelOptionsSubCp: Option[] = [
  // { value: "DW", label: "DW", isDisabled: true },
  // { value: "CP", label: "CP", isDisabled: true },
  { value: "EU", label: "EU" },
];

export const billingType: Option[] = [
  { value: BILLING_TYPE.USAGE, label: "Usage Based" },
  { value: BILLING_TYPE.KEY, label: "Subscription Key" },
];

export const timezoneOptions2: Option[] = [
  { value: "PDT", label: "Pacific Daylight Time (PDT)" },
];

const DATE_FORMATS = [
  "yyyy/MM/dd",
  "yyyy-MM-dd",
  "MM/dd/yyyy",
  "MM-dd-yyyy",
  "dd/MM/yyyy",
  "dd-MM-yyyy",
] as const;

export const dateFormatOptions: Option[] = DATE_FORMATS.map((format) => ({
  value: format,
  label: `${format.replace("YYYY", "yyyy")}(${getAccountTimeFormatDate(
    new Date().toISOString(),
    {
      ...initialAccountState,
      dateFormat: format,
    },
    false
  )})`,
}));

export const timeFormatOptions: Option[] = [
  { value: "HH:mm:ss", label: "HH:MM:SS(13:12:13)" },
  { value: "hh:mm:ss a", label: "hh:MM:SS(1:12:13 PM)" },
  { value: "HH:mm", label: "HH:MM(13:32)" },
  { value: "hh:mm a", label: "hh:MM(1:32 PM)" },
];

export const timezoneOptions: Option[] = getTimeZoneOptions();

export const widgetChartOptions: Option[] = [
  { value: "list", label: "List" },
  { value: "graph", label: "Graph" },
];

export const pricingOptions: Option[] = [
  { value: "won", label: "won" },
  { value: "dollar", label: "$" },
];

export const videoShareStatusOptions: Option[] = [
  { value: "open", label: "Open" },
  { value: "ongoing", label: "Ongoing" },
  { value: "closed", label: "Closed" },
];

export const timeUnitsOptions: Option[] = [
  { value: "MINUTE", label: "Minutes" },
  { value: "HOUR", label: "Hour(s)" },
];

export const timeUnitsOptionsDayHour: Option[] = [
  { value: "HOUR", label: "Hour(s)" },
  { value: "DAY", label: "Day(s)" },
];

export const accessDurationOptions: Option[] = [
  { value: String(60 * 1), label: "1 Hour" },
  { value: String(60 * 4), label: "4 Hours" },
  { value: String(60 * 8), label: "8 Hours" },
  { value: String(60 * 24), label: "1 Day" },
  { value: String(60 * 24 * 7), label: "1 Week" },
  { value: String(60 * 24 * 30), label: "1 Month" },
  { value: String(60 * 24 * 30 * 6), label: "6 Months" },
  { value: String(60 * 24 * 365), label: "1 Year" },
  { value: String(0), label: "Permanent" },
];

export const alertStatusOptions: Option[] = [
  { value: "Active", label: "Active" },
  // { value: "ASSIGNED", label: "Assigned" },
  { value: "Closed", label: "Closed" },
];

export const alertTypeOptions: Option[] = [
  { value: "notresponse", label: "Not Responding" },
  { value: "videoloss", label: "Video Loss [CH4]" },
];

export const settingStartPageOptions: Option[] = [
  { value: "MapView", label: "Map" },
  { value: "ChartView", label: "Widgets" },
  // { value: 'AdminView', label: 'Stats' },
  // { value: "AccountList", label: "Account List" },
  // { value: "RecorderList", label: "Recorder List" },
];

export const recipentsOptions: Option[] = [
  { value: "ALL", label: "All" },
  { value: "USER", label: "User" },
  // { value: "GROUP", label: "Group" },
  { value: "NEWSLETTER", label: "Newsletter" },
];

export const deviceOptions: Option[] = [
  { value: "all", label: "All Devices" },
  { value: "Linux", label: "Linux" },
  { value: "Windows", label: "Windows" },
  { value: "Mac", label: "Mac" },
  { value: "iOS", label: "iOS" },
];

export const responseSpeedOptions: Option[] = [
  { value: "all", label: "All Alerts" },
  { value: "video", label: "Video" },
  { value: "system", label: "System" },
  { value: "disk", label: "Disk" },
  // { value: "recording", label: "Recording" },
];

// 초기 등록 시간 Options
export const timeOptions: Option[] = createTime24for30min();

// help i am a option
export const iAmAOptions: Option[] = [
  { value: "consumerEU", label: "Consumer End-User" },
  { value: "corporateEU", label: "Corporate End-User" },
  { value: "distributor", label: "Distributor" },
  { value: "installer", label: "Integrator/Installer" },
];

// map status options
export const mapStatusOptions: Option[] = [
  { value: "all", label: "All" },
  { value: "healthy", label: "Healthy" },
  // { value: "warningChosen", label: "Warning" },
  { value: "alert", label: "Alert" },
  { value: "critical", label: "Critical" },
  // { value: "assignedChosen", label: "Assigned" },
];

// map status options
export const recorderLevelOptions: Option[] = [
  { value: "10", label: "Administrator" },
  { value: "11", label: "Viewer" },
  { value: "99", label: "Custom" },
];

export const spectrumRoleLevelOptions: Option[] = [
  { value: "0", label: "Power Users" },
  { value: "1", label: "Advanced Viewers" },
  { value: "2", label: "Viewers" },
  { value: "3", label: "Live Viewers" },
  { value: "4", label: "System Health Viewers" },
  // { value: "99", label: "Custom" },
];

// remove license why
export const whyRemoveLicenseOptions: Option[] = [
  { value: "1", label: "The licenses were too expensive" },
  { value: "2", label: "I want to buy licenses for another recorder instead" },
  { value: "3", label: "I have too many licenses on this recorder" },
  {
    value: "4",
    label: "I'm not satisfied with the features that come with these licenses",
  },
  { value: "5", label: "Other (please specify)" },
];

export const monthOptions: Option[] = [
  {
    value: "1",
    label: "January",
  },
  {
    value: "2",
    label: "Feburary",
  },
  {
    value: "3",
    label: "March",
  },
  {
    value: "4",
    label: "April",
  },
  {
    value: "5",
    label: "May",
  },
  {
    value: "6",
    label: "June",
  },
  {
    value: "7",
    label: "July",
  },
  {
    value: "8",
    label: "August",
  },
  {
    value: "9",
    label: "September",
  },
  {
    value: "10",
    label: "October",
  },
  {
    value: "11",
    label: "November",
  },
  {
    value: "12",
    label: "December",
  },
];

export const yearOptions = () => {
  let tempArr = [];
  for (let i = new Date().getFullYear(); i >= 2020; i--) {
    tempArr.push({
      value: String(i),
      label: String(i),
    });
  }

  return tempArr;
};

export const videoGuideOptions: Option[] = [
  {
    value: "about",
    label: "About myDW",
  },
];

//TechSummit
export const techStatusOptions: Option[] = [
  { value: "open", label: "Open" },
  { value: "inProgress", label: "In-Progress" },
  { value: "closed", label: "Closed" },
];

export const initialKeyTypeOptions: Option[] = [
  {
    value: SUBSCRIPTION_TYPE.STANDARD,
    label: changeServiceName(SUBSCRIPTION_TYPE.STANDARD),
  },
  {
    value: SUBSCRIPTION_TYPE.STORAGE,
    label: changeServiceName(SUBSCRIPTION_TYPE.STORAGE),
  },
];

export const durationOptions: Option[] = [
  { label: "1 Year", value: "1" },
  { label: "2 Year", value: "2" },
  { label: "3 Year", value: "3" },
];

export const storageLocationOptions = [
  { label: "East", value: STORAGE_LOCATION.US_EAST_1, disabled: true },
  { label: "Central", value: STORAGE_LOCATION.US_CENTRAL_2, disabled: false },
  { label: "West", value: STORAGE_LOCATION.EU_WEST_1, disabled: true },
];

export const storageWasabiLocationOptions = [
  { label: "East", value: STORAGE_LOCATION.US_EAST_2, disabled: true },
  { label: "Central", value: STORAGE_LOCATION.US_CENTRAL_2, disabled: true },
  { label: "West", value: STORAGE_LOCATION.US_WEST_2, disabled: false },
];

export const discountTierOptions: { value: DISCOUNT_TIER; label: string }[] = [
  // { value: DISCOUNT_TIER.PLATINUM, label: "PLATINUM" },
  { value: DISCOUNT_TIER.GOLD, label: "GOLD" },
  { value: DISCOUNT_TIER.SILVER, label: "SILVER" },
  { value: DISCOUNT_TIER.AUTHORIZED, label: "AUTHORIZED" },
];

export const businessTypeOptions: Option[] = [
  { value: "Retail", label: "Retail" },
  { value: "Wholesaler", label: "Wholesaler" },
  { value: "Manufacturing", label: "Manufacturing" },
  { value: "Service", label: "Service" },
  { value: "Technology", label: "Technology" },
  { value: "Healthcare", label: "Healthcare" },
  { value: "Education", label: "Education" },
  { value: "Government", label: "Government" },
  { value: "Other", label: "Other" },
];

export const modelOptions: Option[] = [
  { value: ADMINUNLOCK_MODEL.N0A, label: "N0A (COVA16)" },
  { value: ADMINUNLOCK_MODEL.N0Z, label: "N0Z (COVA4 / COVA8)" },
  { value: ADMINUNLOCK_MODEL.D1E, label: "D1E (ENC4K16)" },
];
const channels = [1, 4, 8, 16, 24, 48, 100, 200, 500];

/**
 * duration(개월) 값으로 채널 옵션 생성
 * @param duration 구독 기간 (개월 단위: 1, 2, 3, 6, 12, 24, 36 등)
 */
export const standardChannelOptionsByDuration = (
  duration: number
): Option[] => {
  const isYearPlan = duration % 12 === 0 && duration >= 12;

  return channels.map((channel) => {
    if (isYearPlan) {
      const sku = `DWS-myDW${duration}M${channel}C`;
      const yearLabel = `${duration / 12}YR`;

      return {
        value: String(channel), // 채널 값만 value
        label: `${channel}CH: ${sku} (${yearLabel})`,
      };
    } else {
      return {
        value: String(channel),
        label: `${channel}CH`, // 월 단위일 경우
      };
    }
  });
};

export const suspendOptions = [
  {
    label: "Enabled",
    title: "Do you really want to reactivate this organization?",
    subTitle: "Associated organization will also be reactivated.",
    value: 0,
  },
  {
    label: "Suspended",
    title: "Do you really want to suspend this organization?",
    subTitle: "Associated organization will also be suspended.",
    value: 1,
  },
  {
    label: "Disabled",
    title: "Do you really want to disable this organization?",
    subTitle: "Associated organization will also be disabled.",
    value: 2,
  },
];

export const enableImageTypesOption = [
  "image/jpg",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/svg+xml",
];

export const covaModelOptions: Option[] = [
  {
    label: "DW-COVA4xT / DW-COVA8xT",
    value: COVA_MODEL.DW_COVA4,
  },
  { label: "DW-COVA16xT", value: COVA_MODEL.DW_COVA16 },
];

export const firwareAvailableTypeOptions: Option[] = [
  { value: "COVA", label: "COVA" },
];

export const cumulusPartNumberOptions: Option[] = [
  { value: "DWS-CS12M250G", label: "DWS-CS12M250G" },
  { value: "DWS-CS12M500G", label: "DWS-CS12M500G" },
  { value: "DWS-CS12M750G", label: "DWS-CS12M750G" },
  { value: "DWS-CS12M1T", label: "DWS-CS12M1T" },
  { value: "DWS-CS12M2T", label: "DWS-CS12M2T" },
  { value: "DWS-CS12M4T", label: "DWS-CS12M4T" },
  { value: "DWS-CS12M8T", label: "DWS-CS12M8T" },
  { value: "DWS-CS12M12T", label: "DWS-CS12M12T" },
  { value: "DWS-CS12M16T", label: "DWS-CS12M16T" },
  { value: "DWS-CS12M24T", label: "DWS-CS12M24T" },
  { value: "DWS-CS12M48T", label: "DWS-CS12M48T" },
  { value: "DWS-CS12M64T", label: "DWS-CS12M64T" },
  { value: "DWS-CS36M250G", label: "DWS-CS36M250G" },
  { value: "DWS-CS36M500G", label: "DWS-CS36M500G" },
  { value: "DWS-CS36M750G", label: "DWS-CS36M750G" },
  { value: "DWS-CS36M1T", label: "DWS-CS36M1T" },
  { value: "DWS-CS36M2T", label: "DWS-CS36M2T" },
  { value: "DWS-CS36M4T", label: "DWS-CS36M4T" },
  { value: "DWS-CS36M8T", label: "DWS-CS36M8T" },
  { value: "DWS-CS36M12T", label: "DWS-CS36M12T" },
  { value: "DWS-CS36M16T", label: "DWS-CS36M16T" },
  { value: "DWS-CS36M24T", label: "DWS-CS36M24T" },
  { value: "DWS-CS36M48T", label: "DWS-CS36M48T" },
  { value: "DWS-CS36M64T", label: "DWS-CS36M64T" },
];

export const usageTypeOptions: Option[] = [
  {
    value: PLAN_TYPE.CHANNEL,
    label: "Channel Usage",
  },
  {
    value: PLAN_TYPE.STORAGE,
    label: "Storage Usage",
  },
];
